// NameLabelOverlay — Phase 6.9: drawing engine が surfaces する NameLabel
// (`{ x, y, name, uid, seq }`) を canvas と同じ親 wrapper の中に DOM 散布する.
//
// 設計上の責務分離 (architecture.md §16 / §19.6):
//   - drawing engine: state (mode + labels) と listener API を提供する.
//     pixel への描画はしない (旧 Java applet は Canvas に直接描いていたが,
//     新版は DOM overlay として分離. これにより zoom / 文字フォント / a11y を
//     CSS で制御できる).
//   - frontend (このファイル): canvas と同じ relative wrapper の中に
//     `<div className="absolute inset-0 pointer-events-none">` を置き, mode が
//     true のとき各 label を `position: absolute` で散布する.
//
// 座標系:
//   - drawing engine が surfaces する `(x, y)` は **canvas (= layer) 座標**.
//   - canvas は `transform: scale(zoom)` で CSS 拡大されるが, この overlay は
//     canvas と兄弟 (同じ wrapper 内) に置く. 親 wrapper の左上を原点として
//     `left: x * zoom`, `top: y * zoom` で配置する (= canvas pixel 座標を
//     CSS px に変換). overlay 自身は scale しない (フォントサイズを zoom 追従
//     させない要件のため).
//
// フォントサイズ: zoom 追従しない (固定 11px). 旧版 `PaintToolCanvas.drawNameLabel`
// (PaintToolCanvas.java の WhiteboardCanvas:1054-1086 参考) は黒文字 + 半透明白枠
// の縁取りで読みやすさを担保していた. CSS では `text-shadow` の 4 方向 white で
// 簡易再現する (背景画像が暗くても黒文字が読める).
//
// mode === false のときは何も描画しない (overlay 自体を null return).
//
// カスタムカーソル (custom-cursor-2026-09-19.md §2.5-2): 描画中の相手の名前の
// 左にその人の確定済みカーソルを出す。位置は名前ラベルと同じ (= stroke segment
// 由来)。drawing engine (drawing-core) は一切触らない (= このファイルは
// presentation 層のみ)。
//
// 2026-09-19 jitter fix (staging 報告「相手のカーソルがぶれて見える」):
//   - 本体は整数 px (`Math.round`) に配置する。
//   - 本体は `left/top` ではなく `translate3d(x, y, 0)` + `transition: transform
//     70ms linear` で滑らかに追従させる。**同じ (uid, seq) の間は React key が
//     変わらない**ため、この component インスタンスは stroke 中ずっと生き続け、
//     mount 直後だけ transition を切る (突然の初期位置への飛びを防ぐ) ことで
//     「新しいストロークの開始位置へは瞬時に、その後の追従は滑らかに」を両立する。
//   - `decodeCursorPixels` を `useMemo` で cursor の参照が変わらない限り再実行しない.
//     旧実装は毎レンダー (= 高頻度な位置更新のたび) に新しい pixel 配列を作って
//     おり、`CustomCursorCanvas` の描画 effect が pixels の参照変化を検知して
//     不要な再描画を繰り返していた (ちらつき・タイミングのズレの一因)。
//
// 2026-09-19 残像 (afterimage) 撤去 (作者判断): 「線の先端にカーソルがあれば
// いい、残像はいらない」— 本体の滑らか追従のみ残し、trail 表示は削除した。
//
// 2026-09-19 follow-up (作者 staging フィードバック「表示位置を自分と相手で統一
// したい」): 旧実装は「名前ラベルの左」(= ラベル自体が既にストローク先端の少し
// 上にオフセットされた位置) を基準にしていたため、自分側 (`BrushCursorOverlay`、
// ペン先の右下) と見える位置が食い違っていた。共通ルールは「カーソル画像の左上を
// ペン先の右下 `(+CUSTOM_CURSOR_HOTSPOT_OFFSET_PX, +同値)`」(shared/customCursorDraw.ts
// の 1 つの定数) — 名前ラベル自体の位置計算 (`computeNameLabelPosition`) は不変、
// カーソルだけ「ラベルの左」ではなく「ストローク先端そのもの」を基準に計算し直す。

import type { CustomCursor } from '@oekaki/protocol';
import { useEffect, useMemo, useRef } from 'react';

import type { NameLabel } from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
import {
  COUNTER_ROTATE_TRANSFORM,
  canvasToLocalByZoomFlip,
} from '../../../infrastructure/drawing/viewport.js';
import { CustomCursorCanvas } from '../shared/CustomCursorCanvas.js';
import { CUSTOM_CURSOR_HOTSPOT_OFFSET_PX } from '../shared/customCursorDraw.js';
import { decodeCursorPixels } from '../shared/customCursorPixels.js';

/** CSS px size of the peer cursor thumbnail (own cursor uses `OWN_CURSOR_THUMB_SIZE_PX`, `BrushCursorOverlay.tsx`). */
const PEER_CURSOR_THUMB_SIZE_PX = 20;

export interface NameLabelOverlayProps {
  readonly mode: boolean;
  readonly labels: ReadonlyArray<NameLabel>;
  readonly zoom: number;
  /**
   * custom-cursor-2026-09-19.md §2.5-2: uid → 確定カスタムカーソル
   * (`RoomState.members[]` から引く lookup)。`undefined`/`null` を返せば
   * 何も描かない (= 未設定)。draft (編集中) 状態は反映しない — 描画中の
   * 演出はあくまで「確定した自分の見せ方」であって編集中の下書きではない。
   */
  readonly cursorForUid?: (uid: string) => CustomCursor | null | undefined;
  /**
   * 2026-06-07 キャンバス左右反転 (view flip、`docs/_wip/canvas-flip.md` §5).
   * `true` のとき canvas は engine 側の CSS transform で鏡像表示になっているが、
   * この overlay は canvas の反転 transform 下に**無い** (兄弟要素) ため自動反転
   * しない. 反転中は label の X 位置だけを `(canvasWidth - x)` で再マップする
   * (文字自体は鏡像にならないので可読性 OK). 未指定なら反転なし (= 既存挙動).
   */
  readonly flipX?: boolean;
  /** flip の X 再マップに使う canvas (= layer) buffer 幅. flipX が true のとき必須. */
  readonly canvasWidth?: number;
}

/**
 * 表示位置を計算する純粋関数. テスト容易性のため component から分離.
 *
 * 旧版 `WhiteboardCanvas.drawNameLabel` は (x, y) を「文字の baseline 始点」
 * 寄りに配置していたが, DOM `<span>` で `top` を直接渡す方が UI として直感的
 * なので, ストローク始点の **少し上** に label を出す (始点に重ならず, 描いた
 * 線そのものを邪魔しない).
 *
 * `LABEL_VERTICAL_OFFSET_PX` は固定 px (= zoom 追従しない). 拡大時は label が
 * canvas 全体に対して相対的に小さく見えるが, それは仕様 (zoom 追従しない =
 * 読みやすさ優先).
 */
export const LABEL_VERTICAL_OFFSET_PX = 14;

/**
 * ストローク先端 (= NameLabel が指す canvas 座標) を画面 px (zoom 適用後、view
 * flip 順変換後) に変換する純関数。`computeNameLabelPosition` (= 先端の「少し
 * 上」) と `computePeerCursorPosition` (= 先端の「右下」) の共通の起点。
 */
const computeStrokeTipScreenPosition = (
  label: NameLabel,
  zoom: number,
  // 2026-06-07 view flip (`docs/_wip/canvas-flip.md` §5): 反転中は label の真の
  // canvas X を `displayX(x) = canvasWidth - x` で画面位置へ順変換してから zoom を
  // 掛ける. canvasWidth 未指定なら反転しない (= 既存挙動を不変に保つ防御).
  flipX = false,
  canvasWidth?: number,
): { left: number; top: number } => {
  // 変換は viewport.ts §1b (canvas → wrapper-local) に集約済み。
  const p = canvasToLocalByZoomFlip(label.x, label.y, zoom, flipX, canvasWidth);
  return { left: p.x, top: p.y };
};

export const computeNameLabelPosition = (
  label: NameLabel,
  zoom: number,
  flipX = false,
  canvasWidth?: number,
): { left: number; top: number } => {
  const tip = computeStrokeTipScreenPosition(label, zoom, flipX, canvasWidth);
  return {
    left: tip.left,
    // ストローク始点の少し上 (= 線そのものに重ならない). zoom 追従しない fixed px.
    top: Math.max(0, tip.top - LABEL_VERTICAL_OFFSET_PX),
  };
};

/**
 * Position of the peer cursor thumbnail — the image's **top-left corner**
 * sits at the stroke tip's `(+CUSTOM_CURSOR_HOTSPOT_OFFSET_PX,
 * +CUSTOM_CURSOR_HOTSPOT_OFFSET_PX)` (shared/customCursorDraw.ts の 1 つの
 * 定数、self 側の `BrushCursorOverlay` と共通). Pure + exported for the same
 * testability reason as `computeNameLabelPosition`.
 *
 * 2026-09-19 follow-up (作者 staging フィードバック): 旧実装は名前ラベルの位置
 * (= 先端の「少し上」) を起点に「ラベルの左」へオフセットしていたが、自分側と
 * 位置が食い違うため、**名前ラベルではなくストローク先端そのもの**を起点にする
 * (名前ラベル自体の見た目・位置計算は不変).
 */
export const computePeerCursorPosition = (
  label: NameLabel,
  zoom: number,
  flipX = false,
  canvasWidth?: number,
): { left: number; top: number } => {
  const tip = computeStrokeTipScreenPosition(label, zoom, flipX, canvasWidth);
  return {
    left: tip.left + CUSTOM_CURSOR_HOTSPOT_OFFSET_PX,
    top: tip.top + CUSTOM_CURSOR_HOTSPOT_OFFSET_PX,
  };
};

/**
 * Peer cursor thumbnail for one (uid, seq) group.
 *
 * Kept as its own component (rather than inline JSX in the `.map` below) for
 * two jitter-fix reasons (2026-09-19, see file header):
 *   - `decodeCursorPixels` is memoized on `cursor`'s identity, so it isn't
 *     re-run (and doesn't hand `CustomCursorCanvas` a fresh array reference)
 *     on every high-frequency position update — only when the underlying
 *     cursor itself changes. `CustomCursorCanvas`'s own paint effect depends
 *     on `pixels` identity, so a fresh array every render was forcing an
 *     unnecessary repaint on every stroke segment.
 *   - The live body position needs a "no transition on first mount, smooth
 *     transition after" toggle. The parent keys this whole group by
 *     `${uid}:${seq}` (stable across one stroke's segments, but a fresh key
 *     for each new stroke) — so this component's own mount lifecycle already
 *     lines up exactly with "snap to a new stroke's start position, animate
 *     smoothly within it".
 */
const PeerCursorVisual = ({
  cursor,
  bodyLeft,
  bodyTop,
}: {
  readonly cursor: CustomCursor;
  readonly bodyLeft: number;
  readonly bodyTop: number;
}): React.ReactNode => {
  const pixels = useMemo(() => decodeCursorPixels(cursor), [cursor]);
  // Mount-vs-update transition gating: the very first render of a given
  // (uid, seq) group must NOT transition (it would visibly slide in from
  // the element's default untransformed position), but every subsequent
  // position update within the same stroke should animate smoothly.
  //
  // A ref (not state) is deliberate: flipping it via `setState` would force
  // an extra render right after mount for no visual purpose (the mount
  // frame itself must stay transition-less regardless). Reading `.current`
  // during render — before the effect below flips it — captures "is this
  // the very first commit" without an extra render; the flip only affects
  // whichever render happens *next* (i.e. the following position update).
  const mountedRef = useRef(false);
  const transitionReady = mountedRef.current;
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  // `bodyLeft`/`bodyTop` come straight from `computePeerCursorPosition`
  // (unrounded, zoom-scaled screen px) — round here so the body never sits
  // on a sub-pixel coordinate (jitter fix: `image-rendering: pixelated`
  // thumbnails blur/resample when placed at fractional px).
  const left = Math.round(bodyLeft);
  const top = Math.round(bodyTop);

  return (
    <div
      aria-hidden="true"
      data-oekaki-peer-cursor-thumb
      data-oekaki-peer-cursor-transition-ready={transitionReady ? 'true' : undefined}
      className="absolute top-0 left-0"
      style={{
        // 表示回転 (canvas-rotation 段取り 2): wrapper ごと回るので、ストローク先端 (= この
        // サムネの左上から `-HOTSPOT_OFFSET` の点) を軸に逆回転して画面に対して正立させる。
        transform: `translate3d(${left}px, ${top}px, 0) ${COUNTER_ROTATE_TRANSFORM}`,
        transformOrigin: `${-CUSTOM_CURSOR_HOTSPOT_OFFSET_PX}px ${-CUSTOM_CURSOR_HOTSPOT_OFFSET_PX}px`,
        transition: transitionReady ? 'transform 70ms linear' : 'none',
        willChange: 'transform',
      }}
    >
      <CustomCursorCanvas pixels={pixels} sizePx={PEER_CURSOR_THUMB_SIZE_PX} />
    </div>
  );
};

export const NameLabelOverlay = ({
  mode,
  labels,
  zoom,
  flipX,
  canvasWidth,
  cursorForUid,
}: NameLabelOverlayProps): React.ReactNode => {
  if (!mode) return null;
  if (labels.length === 0) return null;
  return (
    // pointer-events: none で canvas の pointerdown を全く邪魔しない.
    // 親 wrapper (canvas を包む div) は `relative` 必須. 親側で確実に
    // relative になるよう ClassicLayout.tsx 側で wrapper を変更する.
    //
    // overflow を visible のまま放置するのは意図的: canvas は engine の
    // `transform: scale(zoom)` で視覚的に拡大されるが, overlay div 自身は
    // 拡大されないので overlay の inset 範囲 = native canvas size のまま.
    // zoom>1 のときストローク始点の `x * zoom` は overlay box の右下を
    // 突き抜ける可能性があるため, ここで overflow-hidden しないことで label
    // が canvas の拡大像に追従できる (= 親 ClassicLayout 側の overflow-auto
    // が wrapper ごとスクロールさせる).
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 left-0 h-full w-full"
      data-oekaki-namelabel-overlay
    >
      {labels.map((label) => {
        const { left, top } = computeNameLabelPosition(label, zoom, flipX, canvasWidth);
        const cursor = cursorForUid?.(label.uid) ?? null;
        const key = `${label.uid}:${label.seq}`;
        const bodyPos = computePeerCursorPosition(label, zoom, flipX, canvasWidth);
        // 表示回転: ラベルの逆回転の軸 = ストローク先端 (label 左上からの相対位置)。
        const tipTop = bodyPos.top - CUSTOM_CURSOR_HOTSPOT_OFFSET_PX;
        return (
          <div key={key} data-oekaki-namelabel-group>
            {cursor !== null && (
              <PeerCursorVisual cursor={cursor} bodyLeft={bodyPos.left} bodyTop={bodyPos.top} />
            )}
            <span
              // 旧版風 黒文字 + 4方向白縁取り. text-shadow で簡易再現.
              // フォントは固定 11px (zoom 追従しない), 太字, leading-none で
              // span の box が文字に密着するようにする.
              className="absolute font-bold text-[11px] text-black leading-none whitespace-nowrap"
              style={{
                left: `${left}px`,
                top: `${top}px`,
                transform: COUNTER_ROTATE_TRANSFORM,
                transformOrigin: `0px ${tipTop - top}px`,
                textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff',
              }}
            >
              {label.name}
            </span>
          </div>
        );
      })}
    </div>
  );
};
